// ui/composables/useClientCoverages.js
import { ref, shallowRef } from 'vue';

import { viewerToday } from './viewerToday';
import { INTENT } from '../../application/coverageChange';
import { createCoverageUnitOfWork } from '../../application/coverageUnitOfWork';
import { commitCoverages } from '../../application/useCases/commitCoverages';
import { loadCoverageSession } from '../../application/useCases/loadCoverageSession';
import { trackCoverageAdded } from '../../infrastructure/analytics/revenueCycleAnalytics';
import * as coverageRepository from '../../infrastructure/api/coverageApi';
import * as fundingSourceRepository from '../../infrastructure/api/fundingSourceApi';
import * as personRepository from '../../infrastructure/api/personApi';

/**
 * @typedef {Object} CoverageSession
 *
 * One client's coverage editing session — the collaborator `ClientCoveragePanel` renders. Named so
 * the two halves of this context's published interface have a contract between them rather than
 * an understanding.
 *
 * @property {import('vue').Ref<Object[]>} inSequenceCoverages - the billing sequence, in order
 * @property {import('vue').Ref<Object[]>} endedCoverages - past their effective end
 * @property {import('vue').Ref<Object[]>} allCoverages - including tombstones, for validation
 * @property {import('vue').Ref<Set<string>>} pausedCoverageIds - coverages with billing paused
 * @property {import('vue').Ref<Object[]>} fundingSources - the clinic's FundingSources
 * @property {import('vue').Ref<Object>} people - whoever holds a policy on these coverages,
 *   keyed by person id, so a row can judge readiness without a read of its own
 * @property {import('vue').Ref<boolean>} hasPendingChanges
 * @property {import('vue').Ref<boolean>} hasReordered - the user changed the order this session
 * @property {import('vue').Ref<boolean>} isLoading
 * @property {import('vue').Ref<boolean>} isLoaded
 * @property {(scope: { clientId: string|null, clinicId: string }) => Promise<void>} load
 * @property {(attrs: Object) => Object} add - from the form's flat attributes
 * @property {(attrs: Object) => void} update - from the form's flat attributes
 * @property {(id: string, verified: boolean) => void} setEligibilityVerified
 * @property {(id: string, paused: boolean) => void} setBillingPaused - pause or resume billing
 * @property {(id: string) => void} remove
 * @property {(orderedIds: string[]) => void} reorder
 * @property {(id: string, offset: number) => void} move
 * @property {() => void} restoreOrder - put the sequence back the way it was loaded
 * @property {(scope: { clientId: string, clinicId: string }) => Promise<void>} commit
 */

/** The methods a CoverageSession must expose — used to validate one at a component boundary. */
export const COVERAGE_SESSION_METHODS = [
  'load', 'add', 'update', 'setEligibilityVerified', 'setBillingPaused', 'remove', 'reorder', 'move', 'restoreOrder',
  'commit',
];

/**
 * Creates a coverage editing session: a unit of work over a client's coverages, plus load and
 * flush.
 *
 * The unit of work itself stays a plain, non-reactive object — wrapping it in `reactive()` would
 * put Vue proxies inside the domain objects handed to the API layer on flush. Instead every
 * mutation republishes a snapshot into refs, which is what the templates read.
 *
 * Callers own the session's lifetime. In the client dialog that is the dialog itself rather than
 * the coverage tab, because tab panels unmount on switch and staged edits have to outlive that.
 *
 * @returns {CoverageSession}
 */
export function useClientCoverages() {
  /**
   * The one clock read in this session, shared by everything that resolves as-of a date.
   *
   * Fixed so a row cannot jump between the ranked and ended groups mid-edit — and, since funding
   * sources became effective-dated, so the payer picker resolves each funder's version against the
   * same date the coverage rows are judged against. Two clock reads could disagree across midnight.
   */
  const asOf = viewerToday();

  const people = shallowRef({});

  const unitOfWork = shallowRef(createCoverageUnitOfWork({
    newId: coverageRepository.newCoverageId,
    newIntentId: coverageRepository.newBillingIntentId,
    asOf,
  }));

  const repositories = { coverageRepository, fundingSourceRepository, personRepository };

  /**
   * The read in progress, if any. `isLoaded` only flips once a read RESOLVES, so it cannot dedupe
   * concurrent callers on its own — and there are some: the panel loads from a watcher with
   * `immediate`, and it is mounted inside a tab panel that unmounts on switch, so leaving the tab
   * and coming back before the first read lands starts a second one.
   *
   * Two reads is not just waste. Each one calls `unitOfWork.load`, which resets the baseline the
   * reorder note is measured against — so a reorder staged between the two resolutions would stop
   * being reported while it is still staged.
   */
  let inFlight = null;

  const inSequenceCoverages = ref([]);
  const endedCoverages = ref([]);
  const allCoverages = ref([]);
  const fundingSources = ref([]);
  const pausedCoverageIds = ref(new Set());
  const hasPendingChanges = ref(false);
  const hasReordered = ref(false);
  const isLoading = ref(false);
  const isLoaded = ref(false);

  /** Republishes the unit of work's state for the templates. */
  const sync = () => {
    inSequenceCoverages.value = unitOfWork.value.inSequence();
    endedCoverages.value = unitOfWork.value.ended();
    allCoverages.value = unitOfWork.value.all();
    pausedCoverageIds.value = unitOfWork.value.paused();
    hasPendingChanges.value = unitOfWork.value.hasPendingChanges();
    hasReordered.value = unitOfWork.value.hasReordered();
  };

  /**
   * Loads the session. Safe to call on every tab visit and safe to call concurrently — it reads
   * once, and keeps whatever the user has already staged.
   *
   * @param {{ clientId: string|null, clinicId: string }} scope
   */
  async function load({ clientId, clinicId }) {
    if (isLoaded.value) {
      return undefined;
    }
    // A session belongs to one client for its whole life, so a concurrent caller wants the read
    // already running, not another one.
    if (inFlight !== null) {
      return inFlight;
    }

    isLoading.value = true;
    inFlight = (async () => {
      try {
        const loaded = await loadCoverageSession({ clientId, clinicId, asOf }, repositories);

        fundingSources.value = loaded.fundingSources;
        people.value = loaded.people;
        unitOfWork.value.load(loaded.coverages);
        unitOfWork.value.loadPaused(loaded.openBillingIntents);
        isLoaded.value = true;
        sync();
      } finally {
        // Cleared either way: a failed read leaves `isLoaded` false, so reopening the tab retries.
        isLoading.value = false;
        inFlight = null;
      }
    })();

    return inFlight;
  } // load()

  function add(attrs) {
    const coverage = unitOfWork.value.add(attrs);
    sync();
    return coverage;
  } // add()

  function update(attrs) {
    unitOfWork.value.update(attrs);
    sync();
  } // update()

  function setBillingPaused(id, paused) {
    unitOfWork.value.setBillingPaused(id, paused);
    sync();
  } // setBillingPaused()

  function setEligibilityVerified(id, verified) {
    unitOfWork.value.setEligibilityVerified(id, verified);
    sync();
  } // setEligibilityVerified()

  function remove(id) {
    unitOfWork.value.remove(id);
    sync();
  } // remove()

  function reorder(orderedIds) {
    unitOfWork.value.reorder(orderedIds);
    sync();
  } // reorder()

  function move(id, offset) {
    unitOfWork.value.move(id, offset);
    sync();
  } // move()

  function restoreOrder() {
    unitOfWork.value.restoreLoadedOrder();
    sync();
  } // restoreOrder()

  /**
   * Flushes staged coverage edits. Called by the host after the client id is known.
   *
   * @param {{ clientId: string, clinicId: string }} scope
   */
  async function commit(scope) {
    // Read before the flush, which clears the staged changes once it lands.
    const added = unitOfWork.value.changes()
      .filter(({ intent }) => intent === INTENT.CREATE)
      .map(({ coverage }) => coverage);

    await commitCoverages(unitOfWork.value, scope, coverageRepository);
    sync();

    added.forEach(({ fundingSourceId }) => trackCoverageAdded({ clinicId: scope.clinicId, fundingSourceId }));
  } // commit()

  return {
    inSequenceCoverages,
    endedCoverages,
    allCoverages,
    pausedCoverageIds,
    fundingSources,
    people,
    hasPendingChanges,
    hasReordered,
    isLoading,
    isLoaded,
    load,
    add,
    update,
    setEligibilityVerified,
    setBillingPaused,
    remove,
    reorder,
    move,
    restoreOrder,
    commit,
  };
} // useClientCoverages()
